/* eslint-disable unused-imports/no-unused-vars */
import React from 'react';
import {
  Box,
  FormControl,
  // Select,
  MenuItem,
  styled,
  Switch,
  Grid,
  FormControlLabel,
  Slider
} from '@mui/material';
import TextField from '@/components/text-field/text-field';
import Select from '@/components/select/select';
import Typography from '@/components/typography/typography';
import UploadMedia from '@/components/upload-media/upload-media';
import AlignmentControl from './AlignmentControl';
import BorderControl from './border-control';

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 16,
      height: 16,
    },
    '&::before': {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main),
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    '&::after': {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main),
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 12,
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    width: 16,
    height: 16,
    margin: 2,
    // color: theme.palette.theme.primary[800]
  },
  '& .Mui-checked': {
    color: theme.palette.theme.primary[800]
  },
  '& .Mui-checked+.MuiSwitch-track': {
    backgroundColor: `${theme.palette.theme.primary[700]}`,
  },
}));

interface ImageControlsProps {
  attrs: Record<string, any>;
  updateAttrs: (attr: Record<string, any>) => void;
}

const ImageControls: React.FC<ImageControlsProps> = ({
  attrs,
  updateAttrs
}) => {
  const [imageError, setImageError] = React.useState<string>('');
  const { src, alt, styles } = attrs || {}

  const handleAlignmentChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null,
  ) => {
    if (newAlignment) {
      let margin = '0';
      switch (newAlignment) {
        case 'justify':
          margin = '0 auto';
          break;
        case 'right':
          margin = '0 0 0 auto';
          break;
        case 'center':
          margin = '0 auto';
          break;
        case 'left':
          margin = '0 auto 0 0';
          break;
        default:
          margin = '0 auto';
          break;
      }
      updateAttrs({ styles: { margin, alignment: newAlignment } });

    }
  };

  const getAlignment = () => {
    if (styles?.margin === '0 auto' || styles?.alignment === 'center') {
      return 'center';
    } else if (styles?.margin === '0 0 0 auto' || styles?.alignment === 'right') {
      return 'right';
    } else if (styles?.margin === 'auto 0 0 0' || styles?.alignment === 'left') {
      return 'left';
    } else {
      return 'justify';
    }
  };

  return (
    <>
      {/* Image Upload */}
      <Box sx={{ mb: 2 }}>
        <UploadMedia
          type="image"
          label="Upload Image"
          name="imageUpload"
          value={src?.startsWith('data:') ? 'Uploaded Image' : src}
          onChange={(file) => {
            if (file instanceof File) {
              if (file.size > 300 * 1024) {
                setImageError('Please select an image under 300KB.');
                return;
              }
              setImageError('');
              const reader = new FileReader();
              reader.onload = (e) => {
                if (e.target?.result) {
                  updateAttrs({ src: e.target.result as string });
                }
              };
              reader.readAsDataURL(file);
            } else if (!file) {
              updateAttrs({ src: '' });
            }
          }}
          multiple={false}
          renderPreview={() => null}
        />
        {imageError && <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>{imageError}</Typography>}
      </Box>

      {/* Alt Text */}
      <TextField
        label="Alt Text"
        fullWidth
        value={alt}
        onChange={(e) => updateAttrs({ alt: e.target.value })}
        size="small"
        sx={{ mb: 2 }}
      />
      <FormControlLabel
        control={<Android12Switch defaultChecked checked={Boolean(styles?.fullWidth)} />}
        label={<Typography type="s5" color="theme.secondary.800" weight='medium'>Full Width</Typography>}
        labelPlacement="start"
        value={Boolean(styles?.fullWidth)}
        onClick={(e) => {
          const checked = e.target.checked
          updateAttrs({ styles: { fullWidth: checked, width: checked ? 100 : 150, height: checked ? 200 : 150 } })
        }}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: 'auto', mb: 2 }}
      />

      {/* Width Input */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="Width (px)"
            type="number"
            fullWidth
            value={styles?.fullWidth ? '' : (styles?.width !== undefined ? Math.round(Number(styles.width)) : '')}
            onChange={(e) =>
              updateAttrs({ styles: { width: Number(e.target.value) } })
            }
            size="small"
            sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
            disabled={styles?.fullWidth}
          />
        </Grid>
        <Grid item xs={6}>
          {/* Height Input */}
          <TextField
            label="Height (px)"
            type="number"
            fullWidth
            value={styles?.height !== undefined ? Math.round(Number(styles.height)) : ''}
            onChange={(e) =>
              updateAttrs({ styles: { height: Number(e.target.value) } })
            }
            size="small"
            sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
          />

        </Grid>

      </Grid>


      {/* Object Fit Dropdown */}
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" fullWidth>
          <Select
            label='Object Fit'
            value={styles?.objectFit}
            onChange={(e) =>
              updateAttrs({ styles: { objectFit: e.target.value } })
            }
            displayEmpty
          >
            <MenuItem value="contain">Contain</MenuItem>
            <MenuItem value="cover">Cover</MenuItem>
            <MenuItem value="fill">Fill</MenuItem>
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="scale-down">Scale Down</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Opacity */}
      <Box sx={{ mb: 2 }}>
        <Typography type='s5' weight='medium' color='theme.secondary.800' variant="caption">Opacity</Typography>
        <Slider
          value={typeof styles?.opacity === 'number' ? styles.opacity * 100 : 100}
          min={0}
          max={100}
          step={1}
          size="small"
          onChange={(_, value) => updateAttrs({ styles: { opacity: (value as number) / 100 } })}
          valueLabelDisplay="auto"
          sx={(theme: any) => ({
            '& .MuiSlider-rail': {
              color: theme.palette.theme.primary[500],
            },
            '& .MuiSlider-thumb': {
              color: theme.palette.theme.primary[800],
            },
            '& .MuiSlider-track': {
              color: theme.palette.theme.primary[600],
            },
          })}
        />
      </Box>

      {/* Alignment Toggle Buttons */}
      <AlignmentControl
        alignment={getAlignment()}
        onAlignmentChange={handleAlignmentChange}
        displayLabel
      />

      <BorderControl
        styles={styles}
        onChange={(key, value) => updateAttrs({ styles: { [key]: value } })}
        isBordered={styles?.is_bordered}
        disableCollapse
        initiateStyles={(styles) => updateAttrs({ styles })}
      />
    </>
  );
};

export default ImageControls;